/**
 * <s-product-image-zoom> — PDP image zoom preloader.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component only warms the selected zoom image on hover/focus so the
 * fullscreen dialog has the high-resolution image ready when opened.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    this.#setupGalleryNav(opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }


  /** Mobile gallery nav: "1 / N" counter with previous/next arrows synced to the snapped slide. */
  #setupGalleryNav(opts) {
    const gallery = this.querySelector('[data-product-gallery]');
    const nav = this.querySelector('[data-gallery-nav]');
    if (!gallery || !nav) return;
    const total = gallery.children.length;
    const current = nav.querySelector('[data-gallery-current]');
    const prev = nav.querySelector('[data-gallery-prev]');
    const next = nav.querySelector('[data-gallery-next]');
    let index = 0;

    const render = () => {
      if (current) current.textContent = String(index + 1);
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index >= total - 1;
    };

    const goTo = (target) => {
      const clamped = Math.max(0, Math.min(target, total - 1));
      const rtl = getComputedStyle(gallery).direction === 'rtl';
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      gallery.scrollTo({ left: clamped * gallery.clientWidth * (rtl ? -1 : 1), behavior: reduce ? 'auto' : 'smooth' });
    };

    let frame = 0;
    gallery.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const width = gallery.clientWidth || 1;
          index = Math.min(Math.round(Math.abs(gallery.scrollLeft) / width), total - 1);
          render();
        });
      },
      { ...opts, passive: true },
    );

    prev?.addEventListener('click', () => goTo(index - 1), opts);
    next?.addEventListener('click', () => goTo(index + 1), opts);
    render();
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
